import { addDays, differenceInCalendarDays, format, parseISO, startOfWeek } from 'date-fns';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';

const ISO_FORMAT = 'yyyy-MM-dd';

export const snapToMondaySundayWeek = (isoDate: string): { start: string; end: string } | null => {
  if (!isoDate) return null;
  const parsed = parseISO(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  const monday = startOfWeek(parsed, { weekStartsOn: 1 });
  const sunday = addDays(monday, 6);
  return { start: format(monday, ISO_FORMAT), end: format(sunday, ISO_FORMAT) };
};

export const PlanType = {
  STREAMING_DISPLAY: 'streaming-display',
  AUDIO_PODCAST: 'audio-podcast'
} as const;
export type PlanTypeValue = (typeof PlanType)[keyof typeof PlanType];

export type FixedValues = Record<string, string | undefined>;

export interface FixedColumnDef {
  key: string;
  label: string;
}

export const FIXED_COLUMNS: readonly FixedColumnDef[] = [
  { key: 'network', label: 'Network' },
  { key: 'customSegment1', label: 'Custom Segment 1' },
  { key: 'customSegment2', label: 'Custom Segment 2' },
  { key: 'content', label: 'Content' },
  { key: 'additionalDetails', label: 'Additional Details' },
  { key: 'thirdPartyTargeting', label: '3P Targeting' },
  { key: 'platform', label: 'Platform' },
  { key: 'dsp', label: 'DSP' }
];

// Columns whose options aren't sourced from /buyable_streaming_media — they
// stay as a fixed list regardless of selected network.
const STATIC_COLUMN_OPTIONS: Record<string, readonly string[]> = {
  customSegment1: ['Dummy Data'],
  customSegment2: ['Dummy Data'],
  additionalDetails: [':15s', ':30s', ':60s', 'Pause Ads', 'Companion Banner', 'Standard'],
  thirdPartyTargeting: ['None', 'LiveRamp', 'Acxiom', 'Experian', 'Lotame', 'Oracle', 'IRIS.TV']
};

// Row shape returned by GET /buyable_streaming_media?company=<id>.
// One physical network produces multiple rows of different `kind`:
//   - 'derived': company-specific buy with platform/dsp/derivedId
//   - 'globalDescription': global description (network-wide)
//   - 'content': one row per content tag on the network
//   - 'podcast': one row per podcast title on the network
export interface BuyableStreamingMediaRow {
  network: string;
  networkName: string;
  networkGroup: string;
  platform: string | null;
  description: string | null;
  additional: string | null;
  content: string | null;
  podcastTitle: string | null;
  dsp: string | null;
  cpm: number;
  ioMinimum: number;
  lineItemMinimum: number;
  enablePrincipalBasedBuying: boolean;
  derivedId: number | null;
  kind: 'derived' | 'globalDescription' | 'content' | 'podcast';
}

export interface NetworkProfile {
  content: readonly string[];
  platform: readonly string[];
  dsp: readonly string[];
}

export interface MediaCatalog {
  networks: readonly string[];
  profiles: Record<string, NetworkProfile>;
}

export const EMPTY_MEDIA_CATALOG: MediaCatalog = { networks: [], profiles: {} };

interface ProfileSets {
  content: Set<string>;
  platform: Set<string>;
  dsp: Set<string>;
}

// Which row kinds contribute to the option lists for each plan type. Podcast
// rows feed only the Audio & Podcast catalogue; everything else feeds the
// Streaming & Display catalogue.
const PLAN_TYPE_KINDS: Record<PlanTypeValue, ReadonlySet<BuyableStreamingMediaRow['kind']>> = {
  [PlanType.STREAMING_DISPLAY]: new Set(['derived', 'globalDescription', 'content']),
  [PlanType.AUDIO_PODCAST]: new Set(['podcast'])
};

// Classify each network as either an audio/podcast channel or a
// streaming/display channel based on the kinds of rows the API returned for
// it. Networks that surface ANY podcast rows are considered podcast networks
// and won't appear under the Streaming & Display plan type; networks with no
// podcast rows are streaming/display and won't appear under Audio & Podcast.
// Mutually exclusive — a network belongs to exactly one channel even if the
// API returns mixed rows.
const collectPodcastNetworks = (rows: readonly BuyableStreamingMediaRow[]): ReadonlySet<string> => {
  const podcastNetworks = new Set<string>();
  for (const row of rows) {
    if (row.kind === 'podcast') {
      podcastNetworks.add(row.networkName);
    }
  }
  return podcastNetworks;
};

export const buildMediaCatalog = (
  rows: readonly BuyableStreamingMediaRow[],
  planType: PlanTypeValue
): MediaCatalog => {
  const allowedKinds = PLAN_TYPE_KINDS[planType];
  const podcastNetworks = collectPodcastNetworks(rows);
  const isPodcastPlan = planType === PlanType.AUDIO_PODCAST;
  const profileSets: Record<string, ProfileSets> = {};
  for (const row of rows) {
    if (!allowedKinds.has(row.kind)) continue;
    const isPodcastNet = podcastNetworks.has(row.networkName);
    if (isPodcastPlan ? !isPodcastNet : isPodcastNet) continue;
    const name = row.networkName;
    let sets = profileSets[name];
    if (!sets) {
      sets = { content: new Set(), platform: new Set(), dsp: new Set() };
      profileSets[name] = sets;
    }
    if (row.content) {
      sets.content.add(row.content);
    }
    if (row.platform) {
      sets.platform.add(row.platform);
    }
    if (row.dsp) {
      sets.dsp.add(row.dsp);
    }
  }
  const networks = Object.keys(profileSets)
    .filter(name => profileSets[name].platform.size > 0)
    .sort();
  const profiles: Record<string, NetworkProfile> = {};
  for (const name of networks) {
    const sets = profileSets[name];
    profiles[name] = {
      content: [...sets.content].sort(),
      platform: [...sets.platform].sort(),
      dsp: [...sets.dsp].sort()
    };
  }
  return { networks, profiles };
};

export const getColumnOptions = (
  colKey: string,
  network: string | undefined,
  catalog: MediaCatalog
): readonly string[] => {
  if (colKey === 'network') return catalog.networks;
  const staticOpts = STATIC_COLUMN_OPTIONS[colKey];
  if (staticOpts) return staticOpts;
  if (!network) return [];
  const profile = catalog.profiles[network];
  if (!profile) return [];
  return profile[colKey as keyof NetworkProfile] ?? [];
};

export interface WeekData {
  cpm?: string;
  investment?: string;
}

export const computeImpressions = (wd: WeekData): number => {
  const cpm = parseFloat(wd.cpm ?? '');
  const investment = parseFloat(wd.investment ?? '');
  if (!Number.isFinite(cpm) || !Number.isFinite(investment) || cpm <= 0) return 0;
  return (investment / cpm) * 1000;
};

export const formatImpressions = (n: number): string =>
  n > 0 ? getFormattedValue({ value: n, type: '#0' }) : '';

export interface Row {
  fixed: FixedValues;
  weekData: WeekData[];
}

export interface Week {
  startDate: string;
  endDate: string;
  adServingEstimate?: string;
  feeEstimate?: string;
  clearanceAssumption?: string;
}

export interface WeekSummary {
  avgCpm: number;
  totalInvestment: number;
  totalImpressions: number;
  bpmAdServing: number;
  bpmFee: number;
  grandTotal: number;
  estimatedSpend: number;
}

export const computeWeekSummary = (rows: Row[], week: Week, weekIndex: number): WeekSummary => {
  let totalInvestment = 0;
  let totalImpressions = 0;
  for (const row of rows) {
    const wd = row.weekData[weekIndex];
    if (!wd) continue;
    const cpm = parseFloat(wd.cpm ?? '');
    const investment = parseFloat(wd.investment ?? '');
    if (!Number.isFinite(cpm) || !Number.isFinite(investment) || cpm <= 0) continue;
    totalInvestment += investment;
    totalImpressions += (investment / cpm) * 1000;
  }
  const avgCpm = totalImpressions > 0 ? (totalInvestment / totalImpressions) * 1000 : 0;

  const adServingEst = parseFloat(week.adServingEstimate ?? '');
  const feeEst = parseFloat(week.feeEstimate ?? '');
  const clearance = parseFloat(week.clearanceAssumption ?? '');
  const bpmAdServing = Number.isFinite(adServingEst) ? totalInvestment * (adServingEst / 100) : 0;
  const bpmFee = Number.isFinite(feeEst) ? totalInvestment * (feeEst / 100) : 0;
  const grandTotal = totalInvestment + bpmAdServing + bpmFee;
  const estimatedSpend = Number.isFinite(clearance) ? grandTotal * (clearance / 100) : 0;

  return {
    avgCpm,
    totalInvestment,
    totalImpressions,
    bpmAdServing,
    bpmFee,
    grandTotal,
    estimatedSpend
  };
};

export interface SavedPlan {
  id: number;
  name: string;
  lastEditor: string;
  lastEditedAt: number;
  planType: PlanTypeValue;
  weeks: Week[];
  rows: Row[];
}

// TODO(streaming-media-planner): persist to API instead of localStorage.
//
// Proposed schema (when we wire this up to the backend):
//
//   streaming_media_plans
//     id              bigint primary key
//     company_id      bigint not null         -- references companies(id); plans are per-client
//     name            text not null
//     plan_type       text not null           -- enum: 'streaming-display' | 'audio-podcast'
//     created_by      text not null           -- user email
//     last_editor     text not null
//     created_at      timestamptz not null default now()
//     last_edited_at  timestamptz not null default now()
//     unique (company_id, name)
//
//   streaming_media_plan_weeks
//     id                       bigint primary key
//     plan_id                  bigint not null references streaming_media_plans(id) on delete cascade
//     week_index               int not null           -- 0-based position in the plan
//     start_date               date not null          -- always a Monday
//     end_date                 date not null          -- always start_date + 6
//     ad_serving_estimate_pct  numeric(6,3)           -- 0..100
//     fee_estimate_pct         numeric(6,3)
//     clearance_assumption_pct numeric(6,3)
//     unique (plan_id, week_index)
//
//   streaming_media_plan_rows
//     id          bigint primary key
//     plan_id     bigint not null references streaming_media_plans(id) on delete cascade
//     row_index   int not null
//     fixed       jsonb not null          -- { network, customSegment1, ..., fepOlv }; keys match FIXED_COLUMNS
//     unique (plan_id, row_index)
//
//   streaming_media_plan_row_weeks
//     row_id      bigint not null references streaming_media_plan_rows(id) on delete cascade
//     week_index  int not null
//     cpm         numeric(10,4)
//     investment  numeric(14,2)
//     primary key (row_id, week_index)
//
// Open question for review: save cadence — debounced auto-save per edit, or
// only on explicit Save Plan / Save Edits? Current localStorage stub saves on
// every action via upsertSavedPlan; for API we should probably batch.
const STORAGE_KEY = 'streamingMediaPlannerSavedPlans';

const isPlanType = (val: unknown): val is PlanTypeValue =>
  val === PlanType.STREAMING_DISPLAY || val === PlanType.AUDIO_PODCAST;

const isSavedPlan = (val: unknown): val is SavedPlan => {
  if (typeof val !== 'object' || val === null) return false;
  const p = val as Record<string, unknown>;
  return (
    typeof p.id === 'number' &&
    typeof p.name === 'string' &&
    typeof p.lastEditor === 'string' &&
    typeof p.lastEditedAt === 'number' &&
    isPlanType(p.planType) &&
    Array.isArray(p.weeks) &&
    Array.isArray(p.rows)
  );
};

export const loadSavedPlans = (): SavedPlan[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedPlan);
  } catch {
    return [];
  }
};

const writeSavedPlans = (plans: SavedPlan[]): void => {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
};

export const upsertSavedPlan = (
  plans: SavedPlan[],
  draft: Omit<SavedPlan, 'id' | 'lastEditedAt'> & { id?: number }
): { plans: SavedPlan[]; saved: SavedPlan } => {
  const now = Date.now();
  if (draft.id !== undefined) {
    const existing = plans.find(p => p.id === draft.id);
    if (existing) {
      const updated: SavedPlan = {
        ...existing,
        name: draft.name,
        lastEditor: draft.lastEditor,
        lastEditedAt: now,
        planType: draft.planType,
        weeks: draft.weeks,
        rows: draft.rows
      };
      const next = plans.map(p => (p.id === draft.id ? updated : p));
      writeSavedPlans(next);
      return { plans: next, saved: updated };
    }
  }
  const nextId = plans.reduce((max, p) => Math.max(max, p.id), 0) + 1;
  const created: SavedPlan = {
    id: nextId,
    name: draft.name,
    lastEditor: draft.lastEditor,
    lastEditedAt: now,
    planType: draft.planType,
    weeks: draft.weeks,
    rows: draft.rows
  };
  const next = [...plans, created];
  writeSavedPlans(next);
  return { plans: next, saved: created };
};

export const deleteSavedPlan = (plans: SavedPlan[], id: number): SavedPlan[] => {
  const next = plans.filter(p => p.id !== id);
  writeSavedPlans(next);
  return next;
};

export const formatLastEdited = (epochMs: number): string => {
  if (!Number.isFinite(epochMs)) return '';
  const d = new Date(epochMs);
  const date = d.toLocaleDateString('en-US', {
    month: 'numeric',
    day: '2-digit',
    year: '2-digit'
  });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
};

export interface DimensionDef {
  key: string;
  label: string;
}

export const DIMENSIONS: readonly DimensionDef[] = [
  { key: 'network', label: 'Network' },
  { key: 'customSegment1', label: 'Custom Segment 1' },
  { key: 'customSegment2', label: 'Custom Segment 2' },
  { key: 'content', label: 'Content' },
  { key: 'additionalDetails', label: 'Additional Details' },
  { key: 'thirdPartyTargeting', label: '3P Targeting' },
  { key: 'platform', label: 'Platform' },
  { key: 'dsp', label: 'DSP' },
  { key: 'programmatic', label: 'Programmatic' },
  { key: 'fepOlv', label: 'FEP/OLV' }
];

export const Metric = {
  INVESTMENT: 'investment',
  IMPRESSIONS: 'impressions',
  AVG_CPM: 'avgCpm',
  BPM_AD_SERVING: 'bpmAdServing',
  BPM_FEE: 'bpmFee',
  GRAND_TOTAL: 'grandTotal',
  ESTIMATED_SPEND: 'estimatedSpend'
} as const;
export type MetricKey = (typeof Metric)[keyof typeof Metric];

export interface MetricDef {
  key: MetricKey;
  label: string;
}

export const METRICS: readonly MetricDef[] = [
  { key: Metric.INVESTMENT, label: 'Investment' },
  { key: Metric.IMPRESSIONS, label: 'Impressions' },
  { key: Metric.AVG_CPM, label: 'Avg CPM' },
  { key: Metric.BPM_AD_SERVING, label: 'BPM Ad Serving' },
  { key: Metric.BPM_FEE, label: 'BPM Fee' },
  { key: Metric.GRAND_TOTAL, label: 'Grand Total' },
  { key: Metric.ESTIMATED_SPEND, label: 'Estimated Spend' }
];

export interface SavedSummary {
  id: number;
  name: string;
  lastEditor: string;
  lastEditedAt: number;
  rangeStart: string;
  rangeEnd: string;
  dimensions: string[];
  metrics: MetricKey[];
}

const SUMMARY_STORAGE_KEY = 'streamingMediaPlannerSavedSummaries';

const isMetricKey = (val: unknown): val is MetricKey =>
  typeof val === 'string' && (Object.values(Metric) as string[]).includes(val);

const isSavedSummary = (val: unknown): val is SavedSummary => {
  if (typeof val !== 'object' || val === null) return false;
  const s = val as Record<string, unknown>;
  return (
    typeof s.id === 'number' &&
    typeof s.name === 'string' &&
    typeof s.lastEditor === 'string' &&
    typeof s.lastEditedAt === 'number' &&
    typeof s.rangeStart === 'string' &&
    typeof s.rangeEnd === 'string' &&
    Array.isArray(s.dimensions) &&
    s.dimensions.every(d => typeof d === 'string') &&
    Array.isArray(s.metrics) &&
    s.metrics.every(isMetricKey)
  );
};

export const loadSavedSummaries = (): SavedSummary[] => {
  try {
    const raw = window.localStorage.getItem(SUMMARY_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedSummary);
  } catch {
    return [];
  }
};

const writeSavedSummaries = (summaries: SavedSummary[]): void => {
  window.localStorage.setItem(SUMMARY_STORAGE_KEY, JSON.stringify(summaries));
};

export const upsertSavedSummary = (
  summaries: SavedSummary[],
  draft: Omit<SavedSummary, 'id' | 'lastEditedAt'> & { id?: number }
): { summaries: SavedSummary[]; saved: SavedSummary } => {
  const now = Date.now();
  if (draft.id !== undefined) {
    const existing = summaries.find(s => s.id === draft.id);
    if (existing) {
      const updated: SavedSummary = {
        ...existing,
        name: draft.name,
        lastEditor: draft.lastEditor,
        lastEditedAt: now,
        rangeStart: draft.rangeStart,
        rangeEnd: draft.rangeEnd,
        dimensions: draft.dimensions,
        metrics: draft.metrics
      };
      const next = summaries.map(s => (s.id === draft.id ? updated : s));
      writeSavedSummaries(next);
      return { summaries: next, saved: updated };
    }
  }
  const nextId = summaries.reduce((max, s) => Math.max(max, s.id), 0) + 1;
  const created: SavedSummary = {
    id: nextId,
    name: draft.name,
    lastEditor: draft.lastEditor,
    lastEditedAt: now,
    rangeStart: draft.rangeStart,
    rangeEnd: draft.rangeEnd,
    dimensions: draft.dimensions,
    metrics: draft.metrics
  };
  const next = [...summaries, created];
  writeSavedSummaries(next);
  return { summaries: next, saved: created };
};

export const deleteSavedSummary = (summaries: SavedSummary[], id: number): SavedSummary[] => {
  const next = summaries.filter(s => s.id !== id);
  writeSavedSummaries(next);
  return next;
};

// Inclusive day overlap between [aStart, aEnd] and [bStart, bEnd]. Returns 0 if no overlap or
// either range is invalid. Both ranges are inclusive on both ends (a Mon-Sun week is 7 days).
export const inclusiveDayOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): number => {
  if (!aStart || !aEnd || !bStart || !bEnd) return 0;
  const a1 = parseISO(aStart);
  const a2 = parseISO(aEnd);
  const b1 = parseISO(bStart);
  const b2 = parseISO(bEnd);
  if ([a1, a2, b1, b2].some(d => Number.isNaN(d.getTime()))) return 0;
  const start = a1 > b1 ? a1 : b1;
  const end = a2 < b2 ? a2 : b2;
  const diff = differenceInCalendarDays(end, start) + 1;
  return diff > 0 ? diff : 0;
};

export interface AggregatedRow {
  dimensions: Record<string, string>;
  totals: Record<MetricKey, number>;
}

const EMPTY_DIM_VALUE = '(Unset)';

const newTotals = (): Record<MetricKey, number> => ({
  [Metric.INVESTMENT]: 0,
  [Metric.IMPRESSIONS]: 0,
  [Metric.AVG_CPM]: 0,
  [Metric.BPM_AD_SERVING]: 0,
  [Metric.BPM_FEE]: 0,
  [Metric.GRAND_TOTAL]: 0,
  [Metric.ESTIMATED_SPEND]: 0
});

// Aggregate planned spend across saved plans for the selected date range, grouped by the chosen
// dimensions. Partial-week overlap is prorated by (overlap days / 7).
export const aggregatePlans = (
  plans: SavedPlan[],
  rangeStart: string,
  rangeEnd: string,
  dimensionKeys: string[]
): AggregatedRow[] => {
  if (!rangeStart || !rangeEnd) return [];

  const groups = new Map<string, AggregatedRow>();

  for (const plan of plans) {
    for (let wi = 0; wi < plan.weeks.length; wi += 1) {
      const week = plan.weeks[wi];
      const overlap = inclusiveDayOverlap(week.startDate, week.endDate, rangeStart, rangeEnd);
      if (overlap <= 0) continue;
      const factor = overlap / 7;

      const adServingPct = parseFloat(week.adServingEstimate ?? '');
      const feePct = parseFloat(week.feeEstimate ?? '');
      const clearancePct = parseFloat(week.clearanceAssumption ?? '');

      for (const row of plan.rows) {
        const wd = row.weekData[wi];
        if (!wd) continue;
        const cpm = parseFloat(wd.cpm ?? '');
        const investmentRaw = parseFloat(wd.investment ?? '');
        if (!Number.isFinite(investmentRaw) || investmentRaw <= 0) continue;

        const investment = investmentRaw * factor;
        const impressions = Number.isFinite(cpm) && cpm > 0 ? (investment / cpm) * 1000 : 0;
        const bpmAdServing = Number.isFinite(adServingPct) ? investment * (adServingPct / 100) : 0;
        const bpmFee = Number.isFinite(feePct) ? investment * (feePct / 100) : 0;
        const grandTotal = investment + bpmAdServing + bpmFee;
        const estimatedSpend = Number.isFinite(clearancePct)
          ? grandTotal * (clearancePct / 100)
          : 0;

        const dims: Record<string, string> = {};
        for (const key of dimensionKeys) {
          dims[key] = row.fixed[key] || EMPTY_DIM_VALUE;
        }
        const groupKey = dimensionKeys.map(k => dims[k]).join('||');
        const existing = groups.get(groupKey) ?? { dimensions: dims, totals: newTotals() };
        existing.totals[Metric.INVESTMENT] += investment;
        existing.totals[Metric.IMPRESSIONS] += impressions;
        existing.totals[Metric.BPM_AD_SERVING] += bpmAdServing;
        existing.totals[Metric.BPM_FEE] += bpmFee;
        existing.totals[Metric.GRAND_TOTAL] += grandTotal;
        existing.totals[Metric.ESTIMATED_SPEND] += estimatedSpend;
        groups.set(groupKey, existing);
      }
    }
  }

  // Derive weighted avg CPM per group from investment and impressions.
  const rows = Array.from(groups.values());
  for (const r of rows) {
    const inv = r.totals[Metric.INVESTMENT];
    const imp = r.totals[Metric.IMPRESSIONS];
    r.totals[Metric.AVG_CPM] = imp > 0 ? (inv / imp) * 1000 : 0;
  }
  return rows;
};
